import { CloseOutlined, CaretRightOutlined, PauseOutlined, SoundOutlined, UndoOutlined } from '@ant-design/icons'
import { Button, Input, Spin, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchAudioEditorContext, fetchAudioWaveformPeaks, type AudioEditorContext } from '../../../api'
import { canvasAudioPlaybackUrl } from './audioGenerationProvider'
import AudioEditorWaveform, { type AudioEditorWaveformHandle } from './AudioEditorWaveform'
import { useAudioEditorDraftInputs } from './audioEditorDraftInputs'
import {
  clampSelectionToSource,
  defaultTrimSelection,
  formatAudioTimeMs,
  formatDurationSecondsMs,
  parseAudioTimeInput,
  parseDurationSecondsInput,
  type TrimSelectionMs,
} from './audioTrimTime'

export type AudioEditorModalRequest = {
  nodeId: string
  assetId: string
  workspaceId: string
}

type Props = {
  open: boolean
  request: AudioEditorModalRequest | null
  onClose: () => void
  onCommitTrim: (payload: {
    sourceNodeId: string
    sourceAssetId: string
    workspaceId: string
    trimStartMs: number
    trimEndMs: number
  }) => void
}

export default function AudioEditorModal({ open, request, onClose, onCommitTrim }: Props) {
  const { t } = useTranslation('canvas')
  const [loading, setLoading] = useState(false)
  const [ctx, setCtx] = useState<AudioEditorContext | null>(null)
  const [peaks, setPeaks] = useState<number[]>([])
  const [waveformFailed, setWaveformFailed] = useState(false)
  const [selection, setSelection] = useState<TrimSelectionMs>({ startMs: 0, endMs: 0 })
  const [defaultSelection, setDefaultSelection] = useState<TrimSelectionMs>({ startMs: 0, endMs: 0 })
  // Throttled-for-display-only state (numeric readout + seek slider) —
  // the actual waveform PLAYHEAD LINE never touches React state/props at
  // all, see `AudioEditorWaveform`'s own `AudioEditorWaveformHandle` doc
  // comment (spec §五 perf requirement).
  const [playheadMs, setPlayheadMs] = useState<number | null>(null)
  const [playing, setPlaying] = useState(false)
  const previewSelectionRef = useRef(false)
  const selectionEndRef = useRef(0)
  const selectionStartRef = useRef(0)
  const [volume, setVolume] = useState(0.85)

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const waveformRef = useRef<AudioEditorWaveformHandle | null>(null)
  const rafRef = useRef<number | null>(null)
  const playbackUrl = useMemo(() => {
    if (!request?.workspaceId || !request.assetId) return null
    return canvasAudioPlaybackUrl(request.workspaceId, request.assetId)
  }, [request])

  useEffect(() => {
    if (!open || !request) return
    let cancelled = false
    setLoading(true)
    setCtx(null)
    setPeaks([])
    setWaveformFailed(false)
    void (async () => {
      try {
        const { data: context } = await fetchAudioEditorContext(request.workspaceId, request.assetId)
        if (cancelled) return
        setCtx(context)
        const def = defaultTrimSelection(context.duration_ms)
        setSelection(def)
        setDefaultSelection(def)
        try {
          const { data: wf } = await fetchAudioWaveformPeaks(request.workspaceId, request.assetId)
          if (!cancelled) setPeaks(wf.peaks || [])
        } catch {
          if (!cancelled) setWaveformFailed(true)
        }
      } catch (err) {
        message.error(t('audioEditor.loadFailed', { defaultValue: '无法加载音频编辑器' }))
        onClose()
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
      const el = audioRef.current
      if (el) {
        el.pause()
        el.src = ''
      }
      setPlayheadMs(null)
      previewSelectionRef.current = false
    }
  }, [open, request, onClose, t])

  useEffect(() => {
    selectionStartRef.current = selection.startMs
    selectionEndRef.current = selection.endMs
  }, [selection.startMs, selection.endMs])

  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    el.volume = volume
  }, [volume])

  const durationMs = ctx?.duration_ms ?? 0
  const durationMsRef = useRef(durationMs)
  durationMsRef.current = durationMs

  // VistoeraTASK-00171 §五 — the ONE place the imperative playhead line
  // gets pushed a new position, from two sources:
  //  1. `timeupdate` (fires during ANY playback, coarse ~4-10Hz) — also
  //     owns the existing "stop at selectionEnd" preview-clip contract
  //     and the throttled numeric readout state.
  //  2. A `requestAnimationFrame` loop, running ONLY while `playing`,
  //     purely for smoother per-frame visual motion between `timeupdate`
  //     ticks — it never touches React state, so it can run at 60fps
  //     with zero re-render cost (spec §五 "避免高频 React 全树 rerender").
  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    const pushPlayhead = (ms: number) => {
      const dur = durationMsRef.current
      waveformRef.current?.setPlayheadRatio(dur > 0 ? ms / dur : 0)
    }
    const onTime = () => {
      const ms = Math.round(el.currentTime * 1000)
      setPlayheadMs(ms)
      pushPlayhead(ms)
      if (previewSelectionRef.current && ms >= selectionEndRef.current) {
        el.pause()
        previewSelectionRef.current = false
      }
    }
    const stopRaf = () => {
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
    const tickRaf = () => {
      pushPlayhead(Math.round(el.currentTime * 1000))
      rafRef.current = window.requestAnimationFrame(tickRaf)
    }
    const onPlay = () => {
      setPlaying(true)
      stopRaf()
      rafRef.current = window.requestAnimationFrame(tickRaf)
    }
    const onPauseOrEnded = () => {
      setPlaying(false)
      stopRaf()
      pushPlayhead(Math.round(el.currentTime * 1000))
    }
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('play', onPlay)
    el.addEventListener('pause', onPauseOrEnded)
    el.addEventListener('ended', onPauseOrEnded)
    // Initial paint (e.g. right after load) — playhead starts at 0, not
    // hidden, matching "暂停时停在当前播放位置" for the pre-play state too.
    pushPlayhead(Math.round(el.currentTime * 1000))
    return () => {
      stopRaf()
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('play', onPlay)
      el.removeEventListener('pause', onPauseOrEnded)
      el.removeEventListener('ended', onPauseOrEnded)
    }
  }, [playbackUrl])

  const updateSelection = useCallback(
    (next: TrimSelectionMs) => {
      if (!durationMs) return
      setSelection(clampSelectionToSource(next, durationMs))
    },
    [durationMs],
  )

  const draft = useAudioEditorDraftInputs(selection, durationMs, updateSelection)

  const togglePlay = useCallback(() => {
    const el = audioRef.current
    if (!el) return
    if (el.paused) {
      previewSelectionRef.current = false
      void el.play()
    } else {
      el.pause()
    }
  }, [])

  const previewClip = useCallback(() => {
    const el = audioRef.current
    if (!el) return
    const startSec = selectionStartRef.current / 1000
    el.pause()
    previewSelectionRef.current = true
    const beginPlay = () => {
      void el.play().catch(() => {
        previewSelectionRef.current = false
      })
    }
    if (Math.abs(el.currentTime - startSec) < 0.05) {
      beginPlay()
      return
    }
    const onSeeked = () => {
      el.removeEventListener('seeked', onSeeked)
      beginPlay()
    }
    el.addEventListener('seeked', onSeeked)
    el.currentTime = startSec
  }, [])

  const resetSelection = useCallback(() => {
    setSelection(defaultSelection)
  }, [defaultSelection])

  const handleSave = useCallback(() => {
    if (!request || !ctx) return
    const startMs = parseAudioTimeInput(draft.startDraft)
    const durMs = parseDurationSecondsInput(draft.durationDraft)
    const endFromDraft = parseAudioTimeInput(draft.endDraft)
    let endMs = endFromDraft
    if (startMs != null && durMs != null) {
      endMs = startMs + durMs
    } else if (startMs != null && endFromDraft != null) {
      endMs = endFromDraft
    }
    if (startMs == null || endMs == null || endMs <= startMs) {
      message.error(t('audioEditor.invalidRange', { defaultValue: '剪辑区间无效' }))
      return
    }
    const clamped = clampSelectionToSource({ startMs, endMs }, durationMs)
    onCommitTrim({
      sourceNodeId: request.nodeId,
      sourceAssetId: request.assetId,
      workspaceId: request.workspaceId,
      trimStartMs: clamped.startMs,
      trimEndMs: clamped.endMs,
    })
    onClose()
  }, [ctx, draft.endDraft, draft.durationDraft, draft.startDraft, durationMs, onClose, onCommitTrim, request, t])

  if (!open) return null

  const metaLine = ctx
    ? [
        ctx.format_label,
        `${formatAudioTimeMs(ctx.duration_ms)}（${formatDurationSecondsMs(ctx.duration_ms)} 秒）`,
        ctx.sample_rate_hz ? `${(ctx.sample_rate_hz / 1000).toFixed(1)} kHz` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : ''

  return (
    <div className="pcv-audio-editor-overlay" role="dialog" aria-modal="true" data-testid="audio-editor-modal">
      <button type="button" className="pcv-audio-editor-backdrop" aria-label="Close" onClick={onClose} />
      <div className="pcv-audio-editor-panel">
        <header className="pcv-audio-editor-header">
          <div>
            <h2>{t('audioEditor.title', { defaultValue: '编辑音频' })}</h2>
            <p>{t('audioEditor.subtitle', { defaultValue: '从音频中截取片段，保存为新的音频资产。原始音频不会被修改。' })}</p>
          </div>
          <button type="button" className="pcv-audio-editor-close" aria-label="Close" onClick={onClose}>
            <CloseOutlined />
          </button>
        </header>

        {loading ? (
          <div className="pcv-audio-editor-loading">
            <Spin />
          </div>
        ) : ctx ? (
          <>
            <div className="pcv-audio-editor-source-card">
              <span className="pcv-audio-editor-source-icon" aria-hidden>〽</span>
              <div>
                <div className="pcv-audio-editor-source-title">{ctx.name}</div>
                <div className="pcv-audio-editor-source-meta">{metaLine}</div>
              </div>
            </div>

            <AudioEditorWaveform
              ref={waveformRef}
              peaks={peaks}
              durationMs={durationMs}
              selection={selection}
              onSelectionChange={updateSelection}
            />
            {waveformFailed ? (
              <p className="pcv-audio-editor-waveform-fallback-hint">
                {t('audioEditor.waveformFallback', { defaultValue: '波形加载失败，仍可使用时间选区与试听。' })}
              </p>
            ) : null}

            <div className="pcv-audio-editor-inputs">
              <label>
                <span>{t('audioEditor.start', { defaultValue: '开始时间' })}</span>
                <Input
                  value={draft.startDraft}
                  onChange={(e) => draft.setStartDraft(e.target.value)}
                  onBlur={() => draft.commitStart()}
                  onPressEnter={() => draft.commitStart()}
                />
                <small>
                  {t('audioEditor.range', { defaultValue: '范围' })}: 0:00 - {formatAudioTimeMs(Math.max(0, durationMs - 100))}
                </small>
              </label>
              <label className="pcv-audio-editor-duration-field">
                <span>{t('audioEditor.duration', { defaultValue: '时长' })}</span>
                <div className="pcv-audio-editor-duration-input-row">
                  <Input
                    inputMode="decimal"
                    value={draft.durationDraft}
                    onChange={(e) => draft.setDurationDraft(e.target.value)}
                    onBlur={() => draft.commitDuration()}
                    onPressEnter={() => draft.commitDuration()}
                  />
                  <span className="pcv-audio-editor-unit">{t('audioEditor.secondsUnit', { defaultValue: '秒' })}</span>
                </div>
                <small>
                  {t('audioEditor.range', { defaultValue: '范围' })}: &gt; 0 — {formatDurationSecondsMs(durationMs)} 秒
                </small>
              </label>
              <label>
                <span>{t('audioEditor.end', { defaultValue: '结束时间' })}</span>
                <Input
                  value={draft.endDraft}
                  onChange={(e) => draft.setEndDraft(e.target.value)}
                  onBlur={() => draft.commitEnd()}
                  onPressEnter={() => draft.commitEnd()}
                />
                <small>
                  {t('audioEditor.range', { defaultValue: '范围' })}: {formatAudioTimeMs(100)} - {formatAudioTimeMs(durationMs)}
                </small>
              </label>
            </div>

            <div className="pcv-audio-editor-playback">
              {/* VistoeraTASK-00171 §六 — primary media control: enlarged to
               * a real ~40px hit area (was 36px with a visually tiny
               * icon) and now actually toggles its icon/label with
               * playback state (it silently never did before). */}
              <button
                type="button"
                aria-label={playing ? t('audioEditor.pause', { defaultValue: '暂停' }) : t('audioEditor.play', { defaultValue: '播放' })}
                aria-pressed={playing}
                className="pcv-audio-editor-play-btn"
                data-testid="audio-editor-play-button"
                onClick={togglePlay}
              >
                {playing ? <PauseOutlined /> : <CaretRightOutlined />}
              </button>
              {/* §七 — a real, visually-weighted `Button` (border+background+
               * hover/active/focus all come free from the shared antd
               * primitive every other footer action in this modal already
               * uses), never text/hyperlink-styled chrome a tenant can't
               * tell is clickable. */}
              <Button
                icon={<CaretRightOutlined />}
                className="pcv-audio-editor-preview-btn"
                data-testid="audio-editor-preview-selection-button"
                onClick={previewClip}
              >
                {t('audioEditor.previewSelection', { defaultValue: '试听选中片段' })}
              </Button>
              <span className="pcv-audio-editor-playback-time">
                {formatAudioTimeMs(playheadMs ?? 0)} / {formatAudioTimeMs(durationMs)}
              </span>
              <input
                type="range"
                className="pcv-audio-editor-playback-seek"
                min={0}
                max={1000}
                value={durationMs ? Math.round(((playheadMs ?? 0) / durationMs) * 1000) : 0}
                onChange={(e) => {
                  const el = audioRef.current
                  if (!el || !durationMs) return
                  el.currentTime = (durationMs / 1000) * (Number(e.target.value) / 1000)
                }}
              />
              <SoundOutlined />
              <input
                type="range"
                className="pcv-audio-editor-volume"
                min={0}
                max={100}
                value={Math.round(volume * 100)}
                onChange={(e) => setVolume(Number(e.target.value) / 100)}
              />
            </div>

            <footer className="pcv-audio-editor-footer">
              <Button icon={<UndoOutlined />} onClick={resetSelection}>
                {t('audioEditor.reset', { defaultValue: '重置选区' })}
              </Button>
              <div className="pcv-audio-editor-footer-actions">
                <Button onClick={onClose}>{t('common.cancel', { defaultValue: '取消' })}</Button>
                <Button type="primary" onClick={handleSave}>
                  {t('audioEditor.save', { defaultValue: '保存为新音频' })}
                </Button>
              </div>
            </footer>
          </>
        ) : null}

        {playbackUrl ? <audio ref={audioRef} src={playbackUrl} preload="metadata" className="pcv-audio-editor-audio-el" /> : null}
      </div>
    </div>
  )
}
